<template>
  <SearchHistory
    v-if="isModalOpen"
    v-model:search-query="searchQuery"
    v-model:is-modal-open="isModalOpen"
  />
  <div class="layout-search-results">
    <div class="layout-search-results__search-section">
      <div class="container">
        <div class="search-results__top">
          <div class="f-field search-results__top-left">
            <select
              id="searchDomain"
              v-model="selectedDomain"
              class="f-field__input f-field__input--select search-domain-selector"
              :class="{ 'f-field__input--is-filled': selectedDomain }"
              name="searchDomain"
            >
              <option
                value=""
                disabled
                hidden
              >
                {{ t('searchMessage') }}
              </option>
              <option
                v-for="option in searchDomains"
                :key="`search-type-${option}`"
                :value="option"
              >
                {{ t(`searchDomain.${option}`) }}
              </option>
            </select>
          </div>
          <form
            style="margin: 0; width: 100%;"
            role="search"
            class="f-field-search inline-block-child"
            @focusin="isSearchFocused = true"
            @focusout="isSearchFocused = false"
            @keydown.up.exact.prevent.stop="selectPreviousOption"
            @keydown.down.exact.prevent.stop="selectNextOption"
            @keydown.esc.exact.prevent.stop="searchQuery = ''"
          >
            <button
              type="submit"
              class="f-field-search__submit"
              :aria-label="t('search')"
              @click.prevent="updateRoute({ q: searchQuery, domain: selectedDomain, sort: sortCriteria, useStringQuery, ...activeFilters })"
            />
            <input
              ref="searchInput"
              v-model="searchQuery"
              :placeholder="t('search...')"
              type="text"
              role="combobox"
              :disabled="usingAdvancedSearch"
              :aria-expanded="String(!isListboxHidden)"
              class="f-field-search__input f-field-search__input--card search-input"
              aria-controls="search-listbox"
              :aria-activedescendant="`search-listbox-option-${selectedDomain}`"
              @keydown.enter.prevent="updateRoute({ q: searchQuery, domain: selectedDomain, sort: sortCriteria, useStringQuery, ...activeFilters })"
            >
            <button
              type="history"
              style="width: 2.5rem; height: 2.5rem;"
              :aria-label="History"
              @click.prevent="showHistory"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M16.0001 18L20.0001 22L16.0001 18Z"
                  fill="#45555f"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M16.0001 18L16.0001 11.0078L16.0001 18Z"
                  fill="#45555f"
                />
                <path
                  d="M11.001 12.7089L4.09326 12.709L4.09229 5.81331M5.96091 11.5905C8.79742 5.9256 15.6299 3.60306 21.2216 6.40294C26.8133 9.20283 29.0469 16.0649 26.2104 21.7298C23.3739 27.3946 16.5414 29.7172 10.9497 26.9173C9.48592 26.2989 6.29199 24.2628 5.22634 21.0658M20.0001 22L16.0001 18L16.0001 11.0078"
                  stroke="#45555f"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </form>
        </div>
        <div v-show="selectedDomain === 'records'">
          <div class="f-field f-field--toggle">
            <div class="advanced-search">
              <input
                id="advancedSearchOperator"
                v-model="useStringQuery"
                type="checkbox"
                class="f-field__input--toggle"
                @change="updateRoute({ q: searchQuery, domain: selectedDomain, sort: sortCriteria, useStringQuery, ...activeFilters })"
              >
              <div class="f-field__toggle" />
              <label
                for="advancedSearchOperator"
                class="advanced-search__text"
              >
                {{ t('advancedSearch') }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="layout-search-results__main-content">
      <div class="container">
        <div class="layout-search-results__two-columns">
          <main class="layout-search-results__results-section">
            <SearchPaginatedList
              :is-initial-loading="false"
              :is-searching="searchResults === null"
              :query="q"
              :empty-check="emptySearch"
              :page-data="searchResults"
            >
              <template #list-header="{ isSearching }">
                <div class="tab-navigation tab-navigation--filters">
                  <MultiStateCounter
                    :is-loading="false"
                    :is-searching="isSearching"
                    :count="searchResults ? searchResults.totalItems : 0"
                    entries-string="search.results.count"
                    class="tab-navigation__counter tab-navigation__counter--with-filters"
                  />
                  <div
                    v-if="displayedFilters.length > 0"
                    class="active-filters-display"
                  >
                    <div
                      v-for="filter in displayedFilters"
                      :key="filter.key"
                    >
                      <button
                        class="active-filters-display__clear-button"
                        @click="clearFilterCategory(filter.key)"
                      >
                        <span class="active-filters-display__category-title">
                          {{ filter.title }}:
                        </span>
                        <span class="active-filters-display__clear-text">
                          {{ filter.values.join(', ') }}
                          <template v-if="filter.count > filter.values.length">
                            + {{ filter.count - filter.values.length }}
                          </template>
                        </span>
                        <span class="active-filters-display__icon">
                          &times;
                        </span>
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="domain === 'records'"
                    class="f-inline-group tab-navigation__sort-options"
                  >
                    <Dropdown v-if="domain === 'records'">
                      <template #dropdown-trigger>
                        <button class="tab-navigation__options-link">
                          {{ activeSort }}
                        </button>
                      </template>
                      <template #dropdown-panel>
                        <ul class="dropdown-menu dropdown--xs">
                          <li
                            v-for="criteria in ['_score:desc', 'date:desc', 'date:asc']"
                            :key="`sort-by-${criteria}`"
                            class="dropdown-menu__item"
                          >
                            <button
                              class="dropdown-menu__link"
                              @click="changeSortCriteria(criteria)"
                            >
                              {{ sortCriteriaName(criteria) }}
                            </button>
                          </li>
                        </ul>
                      </template>
                    </Dropdown>
                  </div>
                </div>
                <!-- FIXME: transition looks weird -->
                <Transition name="slide">
                  <AdvancedSearchFilters
                    v-if="domain === 'records' && usingAdvancedSearch"
                    v-model="advancedQuery"
                    :q="searchQuery"
                    :types="[...type]"
                    :populate-query="!hasOpenedAdvancedSearch"
                    @open="hasOpenedAdvancedSearch = true"
                    @submit="updateAdvancedSearch({ sort: sortCriteria })"
                  />
                </Transition>
              </template>
              <template #list-skeleton>
                <Component
                  :is="SkeletonComponent"
                  class="card-list"
                  :count="perPage"
                />
              </template>
              <template #list>
                <!-- RECORDS -->
                <ListRecords
                  v-if="domain === 'records'"
                  :records="searchResults.items"
                  :compact="false"
                  :highlight-titles="q ? false : true"
                  :show-actions="false"
                />
                <!-- AUTHORS -->
                <ul
                  v-else-if="domain === 'authors'"
                  class="card card-list"
                >
                  <li
                    v-for="researcher in searchResults.items"
                    :key="researcher.id"
                    class="card-row"
                  >
                    <UserAvatar
                      v-if="researcher.name"
                      :src="researcher.avatar"
                      :name="researcher.name"
                      class="card-row__figure"
                    />
                    <div class="card-row__text">
                      <RouterLink
                        :to="{ name: 'ResearcherPage', params: { userId: researcher.username } }"
                      >
                        <p class="card-row__title h5--ssp">
                          {{ researcher.name }} <span class="card-row__title-meta">⸱ {{ researcher.username }}</span>
                        </p>
                        <p
                          v-if="researcher.affiliations?.length > 0"
                          style="margin: 0;"
                        >
                          {{ t('researcherResults.affiliations') }}:
                          <span
                            v-for="(affiliation, index) in researcher.affiliations.filter(a => a.unit)"
                            :key="affiliation.id"
                          >
                            <RouterLink :to="{ name: 'UnitPage', params: { unitID: affiliation.unit.id } }">
                              <u>{{ affiliation.unit.acronym }}</u>
                            </RouterLink>
                            <span v-if="index !== researcher.affiliations.length - 1">, </span>
                          </span>
                        </p>
                        <p
                          v-if="(researcher.fos?.primary && researcher.fos.primary.length > 0) || (researcher.interests && researcher.interests.length > 0)"
                          style="margin: 0;"
                        >
                          <span v-if="researcher.fos?.primary">
                            <RouterLink :to="{ name: 'ScientificInterestsPage', query: {q: researcher.fos?.primary} }">
                              <!-- eslint-disable-next-line vue/no-v-html -->
                              <u v-html="sanitizeHtml(highlight(researcher.fos?.primary))" />
                            </RouterLink>
                          </span>
                          <span v-if="(researcher.fos?.primary.length > 0) && researcher.interests?.length > 0">, </span>
                          <span
                            v-for="(interest, index) in researcher.interests || []"
                            :key="interest"
                          >
                            <RouterLink :to="{ name: 'ScientificInterestsPage', query: {q: interest} }">
                              <!-- eslint-disable-next-line vue/no-v-html -->
                              <u v-html="sanitizeHtml(highlight(interest))" />
                            </RouterLink>
                            <span v-if="index !== researcher.interests.length - 1">, </span>
                          </span>
                        </p>
                      </RouterLink>
                    </div>
                  </li>
                </ul>
                <!-- UNITS -->
                <ul
                  v-else-if="domain === 'units'"
                  class="card card-list"
                >
                  <li
                    v-for="unit in searchResults.items"
                    :key="unit.id"
                    class="card-row"
                  >
                    <Avatar
                      v-if="unit.name"
                      :name="unit.name"
                      class="card-row__figure"
                    />
                    <div class="card-row__text">
                      <p class="card-row__title h5--ssp">
                        <RouterLink
                          :to="{ name: 'UnitPage', params: { unitID: unit.id } }"
                          class="card-row__link"
                        >
                          {{ unit.name }}
                        </RouterLink>
                      </p>
                    </div>
                  </li>
                </ul>
              </template>
              <template #pagination>
                <PaginationBar
                  :current-page="currentPage"
                  :total-items="searchResults.totalItems"
                  :items-per-page="perPage"
                />
              </template>
              <template #empty-state>
                <Component :is="EmptyStateComponent">
                  <template #title>
                    {{ emptyStateHeader }}
                  </template>
                  <template #description>
                    {{ emptyStateDescription }}
                  </template>
                </Component>
              </template>
            </SearchPaginatedList>
          </main>
          <aside class="layout-search-results__filters-section">
            <transition name="fade">
              <section
                v-if="domain === 'records' && !usingAdvancedSearch"
                :class="{ 'filter-section--is-open': isRecordTypesFilterOpen }"
                class="filter-section"
              >
                <h2 class="filter-section__title filter-section__toggle">
                  {{ t('searchFilters.title') }}
                  <button
                    class="filter-section__toggle-trigger"
                    @click.prevent="toggleRecordTypesFilter()"
                  />
                </h2>
                <SearchDistribution
                  v-if="domain === 'records' && Object.keys(chartData).length > 0"
                  :data="chartData"
                  :start-year="activeFilters.startYear"
                  :end-year="activeFilters.endYear"
                />
                <div
                  v-if="domain === 'records'"
                  class="year-inputs"
                  style="display: flex; align-items: center; justify-content: center; gap: 15px;"
                >
                  <div
                    class="f-field"
                    style="display: flex; flex-direction: column; align-items: center;"
                  >
                    <input
                      id="startYear"
                      v-model.number="activeFilters.startYear"
                      type="number"
                      class="f-field__input"
                      style="width: 110px; height: 45px; padding: 4px 8px; text-align: left; border-width: 2px; border-radius: 3px;"
                      :min="1911"
                      :max="activeFilters.endYear"
                    >
                  </div>
                  <span style="font-size: 35px; font-weight: 200; align-self: center;">–</span>
                  <div
                    class="f-field"
                    style="display: flex; flex-direction: column; align-items: center;"
                  >
                    <input
                      id="endYear"
                      v-model.number="activeFilters.endYear"
                      type="number"
                      class="f-field__input"
                      style="width: 110px; height: 45px; padding: 4px 8px; text-align: left; border-width: 2px; border-radius: 3px"
                      :min="activeFilters.startYear"
                      :max="new Date().getFullYear()"
                    >
                  </div>
                </div>
                <h3 class="filter-section__title">
                  {{ $t('search.results.typologies') }}
                </h3>
                <SearchTypes
                  v-model="activeFilters.recordTypes"
                  :columns="1"
                  :filter-options="fieldValues.types"
                  field-name="fields.record.type.values"
                  class="filter-section__list"
                />
                <h3 class="filter-section__title">
                  {{ $t('search.results.language') }}
                </h3>
                <SearchTypes
                  v-model="activeFilters.language"
                  :columns="1"
                  :filter-options="fieldValues.language"
                  field-name="fields.record.language.values"
                  class="filter-section__list"
                />
                <h3 class="filter-section__title">
                  {{ $t('search.results.rights') }}
                </h3>
                <SearchTypes
                  v-model="activeFilters.rights"
                  :columns="1"
                  :filter-options="fieldValues.rights"
                  field-name="fields.record.rights.values"
                  class="filter-section__list"
                />
                <h3 class="filter-section__title">
                  {{ $t('search.results.subjectFos') }}
                </h3>
                <SearchTypes
                  v-model="activeFilters.subjectFos"
                  :columns="1"
                  :filter-options="fieldValues.subjectFos"
                  field-name="fields.record.subjectFos.values"
                  class="filter-section__list"
                />
              </section>
            </transition>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ListRecords from '#src/components/search/results/ListRecords.vue'
import Avatar from '#src/components/utils/Avatar.vue'
import UserAvatar from '#src/components/utils/UserAvatar.vue'
import useRecordsAPI from '#src/api/records'
import useResearchersAPI from '#src/api/researchers'
import useUnitsAPI from '#src/api/units'
import UnitEmptyState from '#src/components/empty-states/Unit.vue'
import RecordEmptyState from '#src/components/empty-states/Record.vue'
import PersonEmptyState from '#src/components/empty-states/Person.vue'
import UnitSkeleton from '#src/components/loading-skeletons/Unit.vue'
import UserSkeleton from '#src/components/loading-skeletons/User.vue'
import RecordSkeleton from '#src/components/loading-skeletons/Record.vue'
import SearchTypes from '#src/components/search/filters/SearchTypes.vue'
import SearchPaginatedList from '#src/components/utils/SearchPaginatedList.vue'
import SearchDistribution from '#src/components/charts/SearchDistribution.vue' // TODO: Change name
import PaginationBar from '#src/components/utils/PaginationBar.vue'
import MultiStateCounter from '#src/components/utils/MultiStateCounter.vue'
import Dropdown from '#src/components/utils/Dropdown.vue'
import AdvancedSearchFilters from '#src/components/advanced-search/AdvancedSearchFilters.vue'
import * as fieldValues from '@scholar/metadata-utils/main'
import SearchHistory from '#src/components/SearchHistory.vue'

import dayjs from 'dayjs'
import DOMPurify from 'dompurify'
import { useI18n } from 'vue-i18n'
import { useHeadSafe } from '@unhead/vue'
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

const axios = inject('$axios')
const route = useRoute()
const router = useRouter()

onBeforeRouteUpdate(async (to, from, next) => {
  if (to.query.domain !== 'records') {
    usingAdvancedSearch.value = false
  } else {
    usingAdvancedSearch.value = to.query.advancedSearchOpen === 'true'
  }

  searchQuery.value = to.query.q

  if (to.query.sort) sortCriteria.value = to.query.sort
  if (to.query.perPage) perPage.value = Number(to.query.perPage) || 10
  if (to.query.page) currentPage.value = Number(to.query.page) || 1
  if (to.query.useStringQuery) useStringQuery.value = to.query.useStringQuery === 'true' ? true : false

  // props.domain = to.query.domain
  // TODO: Add new filters!
  try {
    await search({ domain: to.query.domain || props.domain, q: to.query.q, recordTypes: to.query.type, page: Number(to.query.page) || 1, sort: to.query.sort })
  } catch (error) {
    if (error.response?.data?.context?.message === 'The query does not have a valid syntax') {
      searchResults.value = []
      invalidQuerySyntax.value = true
    }
  }
  if (!import.meta.env.SSR && to.query.perPage) localStorage.setItem('perPage', Number(to.query.perPage) || 10)
  next()
})

const props = defineProps({
  domain: {
    type: String,
    default: 'records'
  },
  q: {
    type: String,
    required: true
  },
  page: {
    type: Number,
    required: true
  },
  type: {
    type: Array,
    default: () => []
  },
  language: {
    type: Array,
    default: () => []
  },
  rights: {
    type: Array,
    default: () => []
  },
  subjectFos: {
    type: Array,
    default: () => []
  },
  sort: {
    type: String,
    default: () => '_score:desc'
  },
  startYear: {
    type: Number,
    default: 1911
  },
  endYear: {
    type: Number,
    default: new Date().getFullYear()
  },
  openAdvancedSearch: {
    type: Boolean,
    default: false
  },
  useStringQuery: {
    type: Boolean,
    default: false
  }
})

import { onMounted } from 'vue'

const isModalOpen = ref(false)
const queries = ref([])

onMounted(() => {
  if (typeof window !== 'undefined') {
    const allQueries = JSON.parse(localStorage.getItem('queries')) || []
    queries.value = allQueries.reverse()
  }
})

const saveHistory = () => {
  if (typeof window === 'undefined') return

  if (searchQuery.value) {
    const validQuery = searchQuery.value.trim()

    if (localStorage.getItem('latestQuery') === validQuery) {
      return
    }

    let currentQueries = JSON.parse(localStorage.getItem('queries')) || []

    const existingIndex = currentQueries.findIndex(
      item => item.query.toLowerCase() === validQuery.toLowerCase()
    )

    if (existingIndex !== -1) {
      currentQueries.splice(existingIndex, 1)
    }

    const queryID = currentQueries.length > 0 ? currentQueries[currentQueries.length - 1].id + 1 : 1

    const newQuery = {
      id: queryID,
      query: validQuery,
      timestamp: new Date().toISOString()
    }

    currentQueries.push(newQuery)
    localStorage.setItem('queries', JSON.stringify(currentQueries))
    localStorage.setItem('latestQuery', validQuery) // Lock it in

    queries.value = [...currentQueries].reverse()
  }
}

const showHistory = () => {
  isModalOpen.value = true
}

const RecordsAPI = useRecordsAPI(axios)
const ResearchersAPI = useResearchersAPI(axios)
const UnitsAPI = useUnitsAPI(axios)
const searchQuery = ref(props.q)
const isSearchFocused = ref(false)
const isRecordTypesFilterOpen = ref(false)
const currentPage = ref(props.page || 1)
const activeFilters = ref({
  recordTypes: props.type ?? [],
  language: props.language ?? [],
  rights: props.rights ?? [],
  subjectFos: props.subjectFos ?? [],
  startYear: props.startYear,
  endYear: props.endYear
})
const sortCriteria = ref(props.sort ?? '_score:desc')
const searchResults = ref(null)
const useStringQuery = ref(props.useStringQuery)
let navigationTimeout
const usingAdvancedSearch = ref(props.openAdvancedSearch)
const hasOpenedAdvancedSearch = ref(props.openAdvancedSearch)
const advancedQuery = ref({ fields: [], sequence: [], query: null, status: 'default' })
const perPage = import.meta.env.SSR ? ref(10) : ref(Number(localStorage.getItem('perPage')) || 10)
const invalidQuerySyntax = ref(false)
const activeSort = computed(() => {
  if (sortCriteria.value === '_score:desc') {
    return t('search.results.sort.by.relevance')
  }
  if (sortCriteria.value === 'date:desc') {
    return t('search.results.sort.by.more.recent')
  } else {
    return t('search.results.sort.by.older')
  }
})

const searchDomains = computed(() => {
  return ['records', 'authors', 'units']
})
const selectedDomain = ref(props.domain)

const isEmptyQuery = computed(() => {
  return !searchQuery.value || searchQuery.value.length === 0
})
const isListboxHidden = computed(() => {
  return isEmptyQuery.value || !isSearchFocused.value
})

const EmptyStateComponent = computed(() => {
  const emptyStates = { authors: PersonEmptyState, units: UnitEmptyState, records: RecordEmptyState }
  return usingAdvancedSearch.value ? RecordEmptyState : emptyStates[props.domain]
})

const selectPreviousOption = () => {
  const index = Math.max(0, searchDomains.value.indexOf(selectedDomain.value) - 1)
  selectedDomain.value = searchDomains.value[index]
}
const selectNextOption = () => {
  const index = Math.min(searchDomains.value.length - 1, searchDomains.value.indexOf(selectedDomain.value) + 1)
  selectedDomain.value = searchDomains.value[index]
}

const emptyStateHeader = computed(() => {
  return usingAdvancedSearch.value
    ? t('advancedsearch.results.empty.state.header')
    : t(`search.results.empty.state.${props.domain}.header`, { query: props.q })
})
const emptyStateDescription = computed(() => {
  return usingAdvancedSearch.value
    ? t('advancedsearch.results.empty.state.description')
    : t(`search.results.empty.state.${props.domain}.description`, { query: props.q })
})

const emptySearch = computed(() => {
  return () => {
    if (usingAdvancedSearch.value) {
      return true
    } else {
      return props.q === ''
    }
  }
})

const SkeletonComponent = computed(() => {
  return props.domain === 'authors'
    ? UserSkeleton
    : props.domain === 'units'
      ? UnitSkeleton
      : props.domain === 'records'
        ? RecordSkeleton
        : undefined
})

const chartData = computed(() => {
  if (props.domain === 'records' && searchResults.value?.aggregations?.by_year?.buckets) {
    return searchResults.value.aggregations.by_year.buckets
  }
  return {}
})

const displayedFilters = computed(() => {
  const displayMap = [
    {
      key: 'recordTypes',
      title: t('search.results.typologies'),
      baseI18nPath: 'fields.record.type.values'
    },
    {
      key: 'language',
      title: t('search.results.language'),
      baseI18nPath: 'fields.record.language.values'
    },
    {
      key: 'rights',
      title: t('search.results.rights'),
      baseI18nPath: 'fields.record.rights.values'
    },
    {
      key: 'subjectFos',
      title: t('search.results.subjectFos'),
      baseI18nPath: 'fields.record.subjectFos.values'
    }
  ]

  return displayMap
    .map((category) => {
      const activeKeys = activeFilters.value[category.key] // These are the raw keys (e.g., 'eng', 'article')

      if (activeKeys && activeKeys.length > 0) {
        const activeDisplayValues = activeKeys.map((key) => {
          const i18nKey = `${category.baseI18nPath}.${key}`
          return t(i18nKey)
        })

        const displayValues = activeDisplayValues.slice(0, 2)

        return {
          key: category.key,
          title: category.title,
          values: displayValues,
          count: activeKeys.length
        }
      }
      return null
    })
    .filter(Boolean)
})
const clearFilterCategory = (categoryKey) => {
  if (activeFilters.value[categoryKey] && Array.isArray(activeFilters.value[categoryKey])) {
    activeFilters.value[categoryKey] = []
  }
}

let updateTimeout
watch(() => ({ ...activeFilters.value }), () => {
  clearTimeout(updateTimeout)
  updateTimeout = setTimeout(() => updateRoute({
    q: props.q,
    domain: props.domain,
    sort: props.sort,
    useStringQuery: useStringQuery.value,
    ...activeFilters.value
  }), 800)
})

watch(advancedQuery, async () => {
  if (!advancedQuery.value.fields.find(f => f.name === 'title')) {
    clearTimeout(updateTimeout)
    updateTimeout = setTimeout(() => updateRoute({
      q: '',
      domain: props.domain,
      sort: props.sort,
      useStringQuery: useStringQuery.value,
      ...activeFilters.value
    }), 800)
  } else {
    try {
      await search({ domain: props.domain, q: advancedQuery.value, page: currentPage.value, sort: props.sort })
    } catch (error) {
      if (error.response?.data?.context?.message === 'The query does not have a valid syntax') {
        searchResults.value = []
        invalidQuerySyntax.value = true
      }
    }
  }
  sessionStorage.advancedQuery = JSON.stringify(advancedQuery.value)
}, { deep: true })

watch(usingAdvancedSearch, () => {
  sessionStorage.usingAdvancedSearch = usingAdvancedSearch.value
})

const toggleRecordTypesFilter = () => {
  isRecordTypesFilterOpen.value = !isRecordTypesFilterOpen.value
}

const changeSortCriteria = async (criteria) => {
  sortCriteria.value = criteria
  if (usingAdvancedSearch.value) {
    updateRoute({
      q: props.q,
      domain: props.domain,
      sort: criteria,
      useStringQuery: useStringQuery.value,
      ...activeFilters.value
    })
    updateAdvancedSearch({ sort: criteria })
  } else {
    updateRoute({
      q: props.q,
      domain: props.domain,
      sort: criteria,
      useStringQuery: useStringQuery.value,
      ...activeFilters.value
    })
  }
}

const updateRoute = ({ q, domain, sort, useStringQuery, ...activeFilters }) => {
  const { startYear, endYear, recordTypes = [], language = [], rights = [], subjectFos = [] } = activeFilters
  // prevent duplicate navigation
  if (q.trim() === props.q.trim()
    && domain === props.domain
    && areArraysEqual(recordTypes, props.type)
    && areArraysEqual(language, props.language)
    && areArraysEqual(rights, props.rights)
    && areArraysEqual(subjectFos, props.subjectFos)
    && sort === props.sort
    && startYear === props.startYear
    && endYear === props.endYear
    && useStringQuery === props.useStringQuery
  ) {
    return
  }

  invalidQuerySyntax.value = false
  currentPage.value = 1

  router.push({
    query: {
      q: q.trim(),
      domain,
      type: recordTypes,
      language: language,
      rights: rights,
      subjectFos: subjectFos,
      sort,
      useStringQuery,
      startYear,
      endYear
    }
  })
}

const areArraysEqual = (arr1, arr2) => {
  const a1 = Array.isArray(arr1) ? [...arr1].sort() : []
  const a2 = Array.isArray(arr2) ? [...arr2].sort() : []
  return a1.join(',') === a2.join(',')
}

const updateAdvancedSearch = async ({ sort }) => {
  try {
    await search({ page: currentPage.value, sort })
  } catch (error) {
    if (error.response?.data?.context?.message === 'The query does not have a valid syntax') {
      searchResults.value = []
      invalidQuerySyntax.value = true
    }
  }
}

const search = async ({ q, domain, recordTypes, page, sort }) => {
  saveHistory()
  searchResults.value = null

  if (typeof q !== 'string' || usingAdvancedSearch.value) {
    advancedSearch({ q: advancedQuery.value, page, sort })
  } else {
    await regularSearch({ q, domain, recordTypes, page, sort, useStringQuery })
  }
}

const sanitizeHtml = (html) => {
  return DOMPurify.sanitize(html)
}

const regularSearch = async ({ domain, q, page, sort, useStringQuery }) => {
  if (!props.q && sortCriteria.value.startsWith('_score')) {
    sortCriteria.value = 'date:desc'
    route.query.sort = 'date:desc'
  }
  switch (domain) {
    case 'records':
      searchResults.value = await RecordsAPI.list({
        q,
        page,
        sort,
        perPage: perPage.value,
        recordTypes: activeFilters.value.recordTypes,
        language: activeFilters.value.language,
        rights: activeFilters.value.rights,
        subjectFos: activeFilters.value.subjectFos,
        start: activeFilters.value.startYear ? new Date(activeFilters.value.startYear, 0, 1).toISOString().split('T')[0] : undefined,
        end: activeFilters.value.endYear ? new Date(activeFilters.value.endYear, 11, 31).toISOString().split('T')[0] : undefined,
        useStringQuery: useStringQuery.value,
        includeAggregations: true
      })
      break
    case 'authors':
      searchResults.value = await ResearchersAPI.list({
        q,
        page,
        sort: undefined,
        perPage: perPage.value
      })
      searchResults.value.items.map(async (researcher) => {
        if (researcher.fos?.primary) {
          researcher.fos.primary = t(`fields.record.subjectFos.values.${researcher.fos.primary}`)
        }
        if (researcher.affiliations) {
          researcher.affiliations = await Promise.all(researcher.affiliations
            .filter(a => dayjs(`${a.start.year}-${a.start.month}-01`).isBefore(Date.now(), 'month') && (a.end === null || dayjs(`01-${a.end.month}-${a.end.year}`).isAfter(Date.now())))
            .map(async (affiliation) => {
              affiliation.unit = await UnitsAPI.get({ unitID: affiliation.unitId })
              return affiliation
            }))
        }
        if (researcher.fos?.primary && researcher.interests) {
          researcher.interests = [researcher.fos.primary, ...researcher.interests]
          researcher.fos = undefined
        }
        if (researcher.interests?.reduce((acc, interest) => acc + interest.length, 0) > 200) {
          researcher.interests = researcher.interests?.filter(interest => interest.toLowerCase().includes(props.q.toLowerCase()))
        }
        return researcher
      })
      searchResults.value.items = await Promise.all(searchResults.value.items)
      break
    case 'units':
      searchResults.value = await UnitsAPI.list({
        q,
        page,
        sort: undefined,
        perPage: perPage.value
      })
      break
  }
}

const highlight = (string) => {
  const queryRegex = new RegExp(props.q, 'gi')
  return string.replaceAll(queryRegex, '<strong style="font-weight: 700; text-decoration: underline;">$&</strong>')
}

const advancedSearch = async ({ q, page, sort }) => {
  let payload
  if (q.status === 'default') {
    payload = buildSearch(q.fields, getDefaultSequence(q.fields))
  } else {
    payload = buildSearch(q.fields, q.sequence)
  }

  searchResults.value = await RecordsAPI.advancedFind({
    q: payload,
    page,
    sort: [sort],
    perPage: perPage.value
  })
}

// const toggleAdvancedSearch = async () => {
//   const query = { ...route.query, page: 1, advancedSearchOpen: !usingAdvancedSearch.value }
//   router.push({ query })

//   await nextTick()
//   try {
//     await search({ domain: props.domain, q: props.q, recordTypes: props.type, page: currentPage.value, sort: props.sort })
//   } catch (error) {
//     if (error.response?.data?.context?.message === 'The query does not have a valid syntax') {
//       searchResults.value = []
//       invalidQuerySyntax.value = true
//     }
//   }
// }

const sortCriteriaName = (criteria) => {
  if (criteria === '_score:desc') {
    return t('search.results.sort.by.relevance')
  }
  if (criteria === 'date:desc') {
    return t('search.results.sort.by.more.recent')
  }
  if (criteria === 'date:asc') {
    return t('search.results.sort.by.older')
  }
}

const createNode = (value) => {
  let group
  if (['(', ')'].includes(value)) {
    group = 'ParenthesisNode'
  } else if (['and', 'or', 'not'].includes(value)) {
    group = 'OperationNode'
  } else {
    group = 'FieldNode'
  }
  return { value, group }
}
// const expressionStatus = (query) => {
//   const valid = BooleanParser.test(query)
//   if (query === '') {
//     return 'default'
//   } else if (valid) {
//     return 'valid'
//   } else {
//     return 'invalid'
//   }
// }
const getAvailableFieldIndices = (fields) => {
  return fields
    .map((field, idx) => ({ field, idx }))
    .filter(i => !isFieldEmpty(i.field))
    .map(i => i.idx)
}
const getAvailableFields = (fields) => {
  return fields.filter(f => !isFieldEmpty(f))
}
const getQueryFromSequence = (sequence) => {
  return sequence.map(f => f.value).join(' ')
}
// const sequenceFromIndices = (sequence, indices) => {
//   return sequence.filter(f => f.group !== 'FieldNode' || indices.includes(f.value))
// }
const getDefaultSequence = (fields, operator = 'and') => {
  const seq = getAvailableFieldIndices(fields)
    .map(idx => [createNode(idx), createNode(operator)])
    .flat()

  seq.pop()
  return seq
}
const buildSearch = (fields, sequence) => {
  const index = Object.fromEntries(getAvailableFieldIndices(fields)
    .map((val, idx) => [val, idx])
  )

  const newSequence = sequence
    .map((f) => {
      if (f.group === 'FieldNode') {
        return { ...f, value: index[f.value] }
      }
      return f
    })

  return {
    query: getQueryFromSequence(newSequence),
    mapping: getAvailableFields(fields).map(f => ({
      [f.name]: f.value.value,
      option: f.value.option
    }))
  }
}

const isFieldEmpty = (field) => {
  const fieldType = getFieldType(field.name)
  if (field.value.value === null) {
    return true
  }
  const isEmptyObject = obj => Object.keys(obj).length === 0

  switch (fieldType) {
    case 'string-field':
    case 'text-area-field': return /^\s*$/.test(field.value.value)

    case 'keyword-field':
    case 'number-field':
    case 'subject-field':
    case 'enum-field': return field.value.value.length === 0

    case 'date-field': return isEmptyObject(field.value.value.start)
      && (field.value.value.option !== 'between' || isEmptyObject(field.value.value.end))
    case 'user-field': return (Object.keys(field.value.value).length === 0)
      || (field.value.value.name !== undefined && /^\s*$/.test(field.value.value.name))

    case 'boolean': return false
  }
}

const getFieldType = (field) => {
  /* TODO:
        files
        peerreviewed
      */
  if ([
    'title',
    'titleAlternative',
    'degreeName',
    'authorEmail',
    'publisher',
    'citationTitle',
    'citationVolume',
    'citationIssue',
    'citationStartPage',
    'citationEndPage',
    'institutionName',
    'fundingReferences',
    'citationConferencePlace',
    'fundingReferences.funderName',
    'fundingReferences.awardTitle',
    'fundingReferences.fundingStream'
  ].includes(field)) {
    return 'string-field'
  }
  if (['creators', 'editors', 'advisors'].includes(field)) {
    return 'user-field'
  }
  /**
   * keyword-field:
   *  should be similar to subject field, but doesn't
   *  support the 'all' option, because each record only has 1
   *  the following data-types should use this field:
   * integer-fields:
   *  same as keyword fields but input only works with numbers
   * subject-field:
   *  this field is similar to keyword field, but supports 'all'
   *  option because each record can have multiple subjects
   * enum-with-search:
   *  used for every enum-field (to see whether supports 'all' or not)
   */
  if ([
    'tid',
    'identifierUri',
    'issn',
    'doi',
    'govdoc',
    'isbn',
    'publisherVersionUri',
    'fundingReferences.awardNumber',
    'fundingReferences.funderIdentifier'
  ].includes(field)) {
    return 'keyword-field'
  }
  if (['citationEdition', 'pmid', 'wos'].includes(field)) {
    return 'number-field'
  }
  if (['subject'].includes(field)) {
    return 'subject-field'
  }
  if ([
    'rights',
    'licenseCondition',
    'version',
    'language',
    'type',
    'subjectFos'
  ].includes(field)) {
    return 'enum-field'
  }
  if ([
    'dateIssued',
    'dateAccepted',
    'dateAvailable',
    'citationConferenceDateStart',
    'citationConferenceDateEnd'
  ].includes(field)) {
    return 'date-field'
  }
  // FIXME: When pressing enter: "Assertion failed: Input argument is not an HTMLInputElement", it is submitting the form
  if (['description', 'embargofct'].includes(field)) {
    return 'text-area-field'
  }
  if (['hasFiles'].includes(field)) {
    return 'boolean-field'
  }
}

const { t } = useI18n({
  fallbackWarn: false,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  messages: {
    pt: {
      searchMessage: 'Selecionar um campo',
      search: 'Pesquisar',
      searchFilters: {
        title: 'Filtros de pesquisa'
      },
      'search...': 'Pesquisar...',
      searchDomain: {
        records: 'Publicações',
        authors: 'Autores',
        units: 'Instituições/Unidades de Investigação'
      },
      title: 'Resultados da pesquisa por “{query}”',
      description: 'Página com os resultados da pesquisa',
      researcherResults: {
        affiliations: 'Afiliações'
      },
      searchWithExpressions: {
        label: 'Procurar usando expressões estruturadas',
        error: 'A expressão submetida é inválida'
      },
      advancedSearch: 'Procurar usando expressões estruturadas'
    },
    en: {
      searchMessage: 'Select a field',
      search: 'Search',
      'search...': 'Search...',
      searchFilters: {
        title: 'Search filters'
      },
      searchDomain: {
        records: 'Publications',
        authors: 'Authors',
        units: 'Institutions/Research Units'
      },
      title: 'Search results for “{query}”',
      description: 'Page with the results for the search term',
      researcherResults: {
        affiliations: 'Affiliations'
      },
      searchWithExpressions: {
        label: 'Search with expressions',
        error: 'The provided expression is not valid'
      },
      advancedSearch: 'Search using advanced search'
    }
  }
})
const metaInfo = {
  title: t('title', { query: props.q }),
  meta: [{ name: 'description', content: t('description') }]
}
useHeadSafe(metaInfo)
onBeforeUnmount(() => {
  clearTimeout(navigationTimeout)
})

if (!import.meta.env.SSR) {
  if (sessionStorage.advancedQuery && sessionStorage.usingAdvancedSearch !== 'undefined') {
    advancedQuery.value = JSON.parse(sessionStorage.advancedQuery)
  }
  if (sessionStorage.usingAdvancedSearch && sessionStorage.usingAdvancedSearch !== 'undefined') {
    usingAdvancedSearch.value = JSON.parse(sessionStorage.usingAdvancedSearch)
  }
}
try {
  await search({ domain: props.domain, q: props.q, page: currentPage.value })
} catch (error) {
  if (error.response?.data?.context?.message === 'The query does not have a valid syntax') {
    searchResults.value = []
    invalidQuerySyntax.value = true
  }
}

</script>
<style lang="scss">
@use "sass:color";
@use "#src/assets/scss/variables" as *;
@use "#src/assets/scss/animations" as *;

.search-results__top {
  display: flex;
  justify-content: center;
  flex-flow: row nowrap;
  gap: 2rem;
  width: 100%;
  @media screen and (width <= 47.5rem) {
    margin-top: 2rem;
    flex-flow: column nowrap;
    gap: 2rem;
    align-items: flex-start;
  }
}

.search-results__top-left {
  margin: 0 !important;
  width: 45% !important;
  @media screen and (width <= 47.5rem) {
    width: 100% !important;
  }
}

.search-results__bottom {
  width: 100%;
  margin-top: 0.5rem;
  display: block;
}

.search-domain-selector {
  background-color: white !important;
  height: 3.5rem !important;
}

.search-input {
  border: 0.0625rem solid $gray !important;
  box-shadow: none !important;
}

.f-field-search__submit {
  height: 3.5rem;
  z-index: 1;
}

.filter-section {
  height: 3.375rem;
  overflow: hidden;
  @media screen and (width >= 60rem) {
    height: unset;
    overflow: initial;
    margin-bottom: 2rem;
  }
}

.filter-section--is-open {
  height: unset;
  overflow: initial;
}

.results-section__title {
  text-transform: uppercase;
  font-size: 1.125rem;
  @media screen and (width >= 60rem) {
    // extend from h2
    font-size: 1.6875rem;
  }
}

.filter-section__title {
  font-size: 1.125rem;
  font-weight: 600;
  font-family: $primary-font;
  color: $dark;
  margin-bottom: 1rem;
  padding: 1rem 0.5rem 1rem 0;
  position: relative;
  border-bottom: 0.0625rem solid $light-blue;
  @media screen and (width >= 60rem) {
    padding: 0;
    border-bottom: none;
  }
}

.filter-section__toggle {
  @media screen and (width >= 60rem) {
    visibility: hidden;
    pointer-events: none;
  }
}

.filter-section__toggle-trigger {
  width: 100%;
  height: 1.1875rem;
  position: absolute;
  right: 0;
  top: 1.2rem;
  background-image: url("data:image/svg+xml;utf8,%3Csvg width='12' height='7' viewBox='0 0 12 7' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M-372-379h1440v1577H-372z'/%3E%3Cpath d='M6.036562 4.222324L9.553622.705263c.384802-.384802 1.021846-.38868 1.41237.001844.393247.393247.39135 1.022864.001844 1.41237L6.921954 6.165359c-.042268.071475-.09423.138974-.155913.200658-.200636.200636-.46634.29717-.72948.291428-.263137.005741-.528842-.090792-.729479-.291428-.061683-.061684-.113644-.129183-.155913-.200658L1.105287 2.119477C.715781 1.72997.713884 1.100354 1.107131.707107 1.497655.316582 2.1347.32046 2.519501.705263l3.51706 3.51706z' fill='%2345555F'/%3E%3C/g%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 0.875rem 0.875rem;
  background-position: calc(100% - 0.875rem) 0;
  @media screen and (width >= 60rem) {
    visibility: hidden;
    pointer-events: none;
  }
}

.filter-section--is-open .filter-section__toggle-trigger {
  background-image: url("data:image/svg+xml;utf8,%0A%3Csvg width='12px' height='7px' viewBox='0 0 12 7' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Home' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Publicações' transform='translate(-372.000000, -378.000000)'%3E%3Crect x='0' y='0' width='1440' height='1577'%3E%3C/rect%3E%3Cg id='Icons/Icon-Arrow/Slate' transform='translate(378.000000, 382.000000) rotate(-180.000000) translate(-378.000000, -382.000000) translate(366.000000, 370.000000)'%3E%3Cg id='Icons/Arrows/Icon-Arrow-Down/Slate'%3E%3Crect id='bounds' opacity='0' x='0' y='0' width='24' height='24'%3E%3C/rect%3E%3Cg id='Paths' transform='translate(6.000000, 9.000000)' fill='%2345555F'%3E%3Cpath d='M6.03656185,4.22232387 L9.55362273,0.705262995 C9.93842425,0.32046147 10.5754682,0.316582489 10.9659925,0.707106781 C11.3592395,1.10035374 11.3573423,1.72997056 10.9678363,2.11947656 L6.92195428,6.16535857 C6.87968605,6.23683441 6.82772476,6.30433349 6.76604133,6.36601692 C6.56540518,6.56665307 6.29970008,6.66318633 6.03656185,6.65744479 C5.77342362,6.66318633 5.50771852,6.56665307 5.30708237,6.36601692 C5.24539894,6.30433349 5.19343765,6.23683441 5.15116942,6.16535857 L1.10528741,2.11947656 C0.715781412,1.72997056 0.71388424,1.10035374 1.1071312,0.707106781 C1.49765549,0.316582489 2.13469945,0.32046147 2.51950097,0.705262995 L6.03656185,4.22232387 Z' id='Combined-Shape'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.filter-section__list .f-field--checkbox {
  margin: 0.25rem 0;

  &:last-child {
    margin-bottom: 0.5rem;
  }

  @media screen and (width >= 60rem) {
    margin: 0.5rem 0;
  }
}

.tab-navigation--filters {
  gap: 0.75rem;
  min-width: 6.5rem;
  padding-bottom: 0.75rem;
  flex-flow: row nowrap;

  @media screen and (width <= 35rem) {
    flex-flow: column nowrap;

    // justify-items: left;
  }
}

.tab-navigation__counter--with-filters {
  flex-grow: 0 !important;
  min-width: 7rem;
}

.tab-navigation__sort-options {
  min-width: 8rem;
  margin-left: auto;
  height: 100%;
}

.vertical-tab-navigation {
  border-bottom: 0.0625rem solid $light-blue;
  position: relative;
  @media screen and (width >= 60rem) {
    margin-bottom: 3.5rem;
    border-bottom: none;
  }

  &::before {
    @media screen and (width <= 46.875rem) {
      content: "";
      position: absolute;
      background: linear-gradient(-90deg, rgb(255 255 255 / 100%) 20%, rgb(255 255 255 / 0%) 100%);
      width: 1.75rem;
      height: 100%;
      right: 0;
      z-index: 1;
      pointer-events: none;
    }
  }
}

.vertical-tab-navigation__list {
  display: flex;
  flex-flow: row nowrap;

  // justify-content: stretch;
  overflow-x: auto;
  @media screen and (width >= 60rem) {
    flex-flow: column nowrap;
  }
}
.vertical-tab-navigation__item:last-child {
  margin-bottom: 0;
  padding-right: 0.5rem;
  @media screen and (width >= 60rem) {
    padding-right: 0;
  }
}

.vertical-tab-navigation__item--selected .vertical-tab-navigation__link {
  color: $blue;
  &::after {
    background-color: color.change($color: $blue, $alpha: 1);
    transform: scale(1);
  }
  @media screen and (width >= 60rem) {
    color: $dark;
    background-color: $light-blue;
    &::after {
      display: none;
    }
  }
}

.vertical-tab-navigation__link {
  padding: 1rem 0.5rem;
  width: 100%;
  display: block;
  text-align: left;
  position: relative;
  font-weight: 600;
  font-size: 0.875rem;
  color: $gray;
  text-transform: uppercase;
  transition-property: color;
  @include md-transition;

  &::after {
    content: "";
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 0.125rem;
    background-color: color.change($color: $blue, $alpha: 0);
    transform-origin: 50%;
    transform: scale(0.8);
    transition-property: background-color, transform;
    @include md-transition;
  }

  @media screen and (width <= 46.875rem) {
    white-space: nowrap;
  }
}
</style>
<style lang="scss" scoped>
@use "#src/assets/scss/variables" as *;
@use "#src/assets/scss/animations" as *;

.f-inline-group--large-gap {
  gap: 1.2rem;
}

.trigger {
  &::after {
    content: "";
    position: absolute;
    top: 0.5rem;
    right: 0;
    margin-left: 6px;
    width: 0;
    height: 0;
  }
}

.trigger--closed {
  @include md-transition;

  transition-property: color;
  &::after {
    border-style: solid;
    border-width: 4px 4px 0;
    border-color: currentcolor transparent transparent;
  }
}

.trigger--open {
  @include md-transition;

  transition-property: color;
  &::after {
    border-style: solid;
    border-width: 0 4px 4px;
    border-color: transparent transparent currentcolor;
  }
}

.expanded {
  margin-bottom: 1rem;
}

.active-filters-display {
  display: flex;
  flex-grow: 1;
  flex-flow: row wrap;
  gap: 0.5rem;
}

.active-filters-display__clear-button {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.25rem 0.25rem 0.5rem;
  border: 1px solid #C0C4CF;
  border-radius: 0.5rem;
  font-size: 0.9em;
  font-weight: 600;
}

.active-filters-text-regular {
  color: #45555F;
  font-weight: 400;
}

.active-filters-text-semi-bold {
  color: #45555F;
  font-weight: 600;
}

.active-filters-display__category-title {
  color: #45555F;
  font-weight: 400;
  margin-right: 2px;
}

.active-filters-display__clear-text {
  color: #45555F;
  font-weight: 600;
  margin-right: 4px;
}

.active-filters-display__icon {
  font-size: 20px;
  margin-right: 2px;
  color: #8f95a1;
}

.active-filters-display__clear-button:hover {
  background-color: var(--primary-color);
}

.advanced-search {
  display: flex;
  gap: 0.2rem;
  flex-flow: row nowrap;
  align-items: center;
}

.advanced-search__text {
  align-self: center;
  font-weight: 600;
  line-height: 1.5;
  font-size: 0.8rem;
  color: #45555F;
}

.hidden {
  display: none;
}

.history-list {
display: flex;
flex-flow: column nowrap;
gap: 0.5rem;
width:100%
}

.modal--xlg :deep(.modal__panel) {
justify-content: flex-start;
}

.history-title {
  text-align: center;
  margin: 0;
}

.clearHistory {
  margin-left: auto;
  align-items: center;
  justify-content: center;
}

.clearHistory:hover {
  fill: $red;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.history-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.history-item {
display: flex;
flex-flow: row nowrap;
gap: 1rem;
border-radius: 0.5rem;
padding: 0.5rem;
align-items: center;
width:100%;
}

.history-item:hover{
  background-color: $light-blue;
}

.delete-single-entry {
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
  flex-shrink: 0;
}

.delete-single-entry:hover {
  background-color: $light-blue;
}

.delete-single-entry:hover svg path {
  fill: $red;
}

.history-item__text{
  width: 70%;
}
</style>
